/** @jsx jsx */
import React, { useCallback, useMemo } from 'react';
import { jsx } from '@compiled/react';
import noop from 'lodash/noop';
import { components } from '@atlaskit/select';
import type { MenuListProps } from '@atlaskit/react-select';

import { ButtonItem, Section } from '@atlaskit/menu';
import { useIntl } from '@atlassian/jira-intl';
import { useRouterActions } from '@atlassian/react-resource-router';
import { getProjectSettingsIssueTypesUrl } from '@atlassian/jira-issue-type-config-common/src/getProjectSettingsIssueTypesUrl.tsx';
import { ModalTrigger } from '@atlassian/entry-point-modal-trigger';
import { UFOExperience, ExperienceTypes, ExperiencePerformanceTypes } from '@atlassian/ufo';
import { defaultModalProps } from '@atlassian/jira-issue-type-config-common/src/constants.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { RuntimePropsOfEntryPoint } from '@atlassian/jira-entry-point/src/common/types.tsx';
import { asyncEditIssueTypeModalEntrypoint } from '@atlassian/jira-edit-issue-type-modal/entrypoint.tsx';
import {
	UNKNOWN_SOURCE,
	type ConfigureSource,
} from '@atlassian/jira-issue-type-config-common/src/types.tsx';
import type { Option } from '../types.tsx';
import messages from './messages.tsx';

export type MenuWithManageIssueTypesProps = MenuListProps<Option, false> & {
	children: React.ReactNode;
	projectKey: string;
	projectType?: string;
	projectId: string;
	selectedIssueTypeId: string;
	onIssueTypeUpdated?: () => void;
	source?: ConfigureSource;
};

type EditRuntimeProps = RuntimePropsOfEntryPoint<typeof asyncEditIssueTypeModalEntrypoint>;

const getIssueTypeEditExperience = (source?: ConfigureSource) => {
	return new UFOExperience(`${source ?? UNKNOWN_SOURCE}.edit-issue-type`, {
		type: ExperienceTypes.Operation,
		performanceType: ExperiencePerformanceTypes.Custom,
	});
};

export const MenuWithManageIssueTypes = (props: MenuWithManageIssueTypesProps) => {
	const { formatMessage } = useIntl();
	const { push } = useRouterActions();
	const cloudId = useCloudId();

	const {
		children,
		projectKey,
		projectType,
		projectId,
		selectedIssueTypeId,
		onIssueTypeUpdated,
		source,
		...restProps
	} = props;

	// TODO: DEE-8523 Update getProjectSettingsIssueTypesUrl to handle JSW CMP
	// and pipe in projectType
	const projectSettingsUrl = getProjectSettingsIssueTypesUrl({
		projectKey,
		projectType: projectType ?? '',
	});

	const handleManageIssueTypesClick = useCallback(() => {
		push(projectSettingsUrl);
	}, [push, projectSettingsUrl]);

	const handleAddIssueTypeClick = useCallback(() => {
		// TODO: DEE-8377 Implement add issue type trigger
	}, []);

	const handleEditIssueTypeClick = useCallback(() => {
		// TODO: Add analytics
	}, []);

	const ufoExperience = getIssueTypeEditExperience(source);

	const editEntryPointProps: EditRuntimeProps = useMemo(
		() => ({
			projectId: Number(projectId),
			projectKey,
			onClose: noop,
			issueTypeId: selectedIssueTypeId,
			ufoExperience,
			onRefetchIssueTypesWhenIssueTypeUpdated: onIssueTypeUpdated ?? noop,
			calledFrom: source ?? UNKNOWN_SOURCE,
			shouldShowIssueTypeSelect: true,
		}),
		[projectId, projectKey, selectedIssueTypeId, onIssueTypeUpdated, source, ufoExperience],
	);
	const editEntryPointParams = useMemo(
		() => ({
			projectKey,
			cloudId,
		}),
		[projectKey, cloudId],
	);

	return (
		<>
			<components.MenuList {...restProps}>{children}</components.MenuList>
			<Section hasSeparator>
				<ButtonItem onClick={handleAddIssueTypeClick}>
					{formatMessage(messages.addIssueType)}
				</ButtonItem>
				<ModalTrigger
					entryPoint={asyncEditIssueTypeModalEntrypoint}
					entryPointParams={editEntryPointParams}
					entryPointProps={editEntryPointProps}
					modalProps={defaultModalProps}
				>
					{({ ref }) => (
						<ButtonItem onClick={handleEditIssueTypeClick} ref={ref}>
							{formatMessage(messages.editIssueType)}
						</ButtonItem>
					)}
				</ModalTrigger>
				<ButtonItem onClick={handleManageIssueTypesClick}>
					{formatMessage(messages.manageIssueTypes)}
				</ButtonItem>
			</Section>
		</>
	);
};
